import { Suspense, useMemo } from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { createRelayEnvironment } from '@/relay/environment';
import { LoadingState } from '@/components/loading/LoadingState';
import { HomePage } from '@/features/home/pages/HomePage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { TransactionsPage } from '@/features/transactions/TransactionsPage';
import { CheckoutPage } from '@/features/checkout/CheckoutPage';
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage';
import { AuthPage } from '@/features/auth/AuthPage';
import { SignUpPage } from '@/features/auth/SignUpPage';
import { RequireAuth } from '@/features/auth/session/RequireAuth';
import { SessionProvider } from '@/features/auth/session/SessionProvider';
import { OnboardingPage } from '@/features/onboarding/OnboardingPage';
import { FinResumoPage } from '@/features/financas/FinResumoPage';
import { FinExtratoPage } from '@/features/financas/FinExtratoPage';
import { FinParcelasPage } from '@/features/financas/FinParcelasPage';
import { FinFaturasPage } from '@/features/financas/FinFaturasPage';
import { FinCategoriasPage } from '@/features/financas/FinCategoriasPage';
import { FinConexoesPage } from '@/features/financas/FinConexoesPage';
import { FinSyncPage } from '@/features/financas/FinSyncPage';
import { PlaceholderPage } from '@/features/PlaceholderPage';

/**
 * Rotas do painel. As telas ainda usam dados mock; login e cadastro são reais quando o server
 * tem banco (`authStatus.enabled`), e aí as rotas do painel exigem sessão e Finanças exige a allowlist.
 * /relay-demo mantém a query Relay original.
 */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RequireAuth><DashboardPage /></RequireAuth>} />
      <Route path="/transacoes" element={<RequireAuth><TransactionsPage /></RequireAuth>} />
      <Route path="/checkout" element={<RequireAuth><CheckoutPage /></RequireAuth>} />
      <Route path="/integracoes" element={<RequireAuth><IntegrationsPage /></RequireAuth>} />
      <Route path="/configuracoes" element={<RequireAuth><PlaceholderPage title="Configurações" /></RequireAuth>} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/cadastro" element={<SignUpPage />} />
      <Route path="/onboarding" element={<RequireAuth><OnboardingPage /></RequireAuth>} />
      <Route path="/financas" element={<RequireAuth financas><FinResumoPage /></RequireAuth>} />
      <Route path="/financas/extrato" element={<RequireAuth financas><FinExtratoPage /></RequireAuth>} />
      <Route path="/financas/parcelas" element={<RequireAuth financas><FinParcelasPage /></RequireAuth>} />
      <Route path="/financas/faturas" element={<RequireAuth financas><FinFaturasPage /></RequireAuth>} />
      <Route path="/financas/categorias" element={<RequireAuth financas><FinCategoriasPage /></RequireAuth>} />
      <Route path="/financas/conexoes" element={<RequireAuth financas><FinConexoesPage /></RequireAuth>} />
      <Route path="/financas/sync" element={<RequireAuth financas><FinSyncPage /></RequireAuth>} />
      <Route path="/relay-demo" element={<Suspense fallback={<LoadingState />}><HomePage /></Suspense>} />
      <Route path="*" element={<RequireAuth><DashboardPage /></RequireAuth>} />
    </Routes>
  );
}

export default function App() {
  const environment = useMemo(() => createRelayEnvironment(), []);
  return (
    <RelayEnvironmentProvider environment={environment}>
      <SessionProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppRoutes />
        </BrowserRouter>
      </SessionProvider>
    </RelayEnvironmentProvider>
  );
}
